/**
 * Turning a cohort scan into the table you look at, and the file you send on.
 *
 * Kept apart from the view because these are the rules a reader will argue
 * with — which subjects a filter shows, what a CSV row says — and rules that
 * can be argued with should be testable without a browser.
 */

import type { CohortIssue, CohortScan, CohortSubject } from "@/ipc/contract";

/** What the table is showing. Ordered as the questions arrive. */
export type CohortFilter = "all" | "ready" | "problems" | "errors" | "incomplete";

export const FILTERS: { id: CohortFilter; label: string; hint: string }[] = [
  { id: "all", label: "All subjects", hint: "Everything under the folder" },
  { id: "ready", label: "Ready to run", hint: "Has what a DWI run needs, with nothing blocking it" },
  { id: "problems", label: "Needs a look", hint: "Anything with a warning or an error" },
  { id: "errors", label: "Will fail", hint: "An error-level finding: this subject cannot run as it is" },
  { id: "incomplete", label: "Missing a file", hint: "A role the rest of the cohort has" },
];

/**
 * Roles this cohort tracks: the ones somebody actually has. A column for a
 * modality nobody acquired is a column of dashes.
 */
export function rolesInUse(scan: CohortScan): string[] {
  return scan.coverage.filter((c) => c.present > 0).map((c) => c.role);
}

function isIncomplete(subject: CohortSubject, roles: string[]): boolean {
  return roles.some((role) => !subject.roles.includes(role));
}

export function applyFilter(
  scan: CohortScan,
  filter: CohortFilter,
  query: string,
): CohortSubject[] {
  const roles = rolesInUse(scan);
  const term = query.trim().toLowerCase();
  return scan.subjects.filter((s) => {
    if (term && !s.subjectId.toLowerCase().includes(term)) return false;
    switch (filter) {
      case "ready":
        return s.dwiReady;
      case "problems":
        return s.severity !== "ok";
      case "errors":
        return s.severity === "error";
      case "incomplete":
        return isIncomplete(s, roles);
      default:
        return true;
    }
  });
}

export function countFor(scan: CohortScan, filter: CohortFilter): number {
  return applyFilter(scan, filter, "").length;
}

/** The issues worth putting in a narrow cell, worst first. */
export function topIssues(subject: CohortSubject, limit = 2): CohortIssue[] {
  const rank = (i: CohortIssue) => (i.severity === "error" ? 0 : i.severity === "warning" ? 1 : 2);
  return [...subject.issues].sort((a, b) => rank(a) - rank(b)).slice(0, limit);
}

function csvCell(value: string): string {
  // Quote when the value could otherwise break the row, and double any quote
  // inside it. Subject ids and file names are user data and do contain commas.
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * One row per subject, one column per tracked role, and the findings spelled
 * out — the form a supervisor can open in Excel, sort, and send to somebody.
 *
 * Geometry is written per role rather than summarised, because the column that
 * matters is the one where a single subject differs.
 */
export function toCsv(scan: CohortScan): string {
  const roles = rolesInUse(scan);
  const header = [
    "subject_id",
    "dwi_ready",
    "pwi_ready",
    "severity",
    "file_count",
    "size_mb",
    ...roles.map((r) => `has_${r}`),
    ...roles.map((r) => `geometry_${r}`),
    "issue_count",
    "issues",
    "folder",
  ];

  const rows = scan.subjects.map((s) => {
    const byRole = new Map(s.images.map((i) => [i.role, i]));
    return [
      s.subjectId,
      s.dwiReady ? "yes" : "no",
      s.pwiReady ? "yes" : "no",
      s.severity,
      String(s.fileCount),
      (s.sizeBytes / 1_000_000).toFixed(1),
      ...roles.map((r) => (s.roles.includes(r) ? "yes" : "no")),
      ...roles.map((r) => {
        const sig = byRole.get(r)?.signature;
        if (!sig) return "";
        const dim = sig.dim.join("x");
        const vox = sig.voxelUm.map((u) => (u / 100).toFixed(2)).join("x");
        return `${dim} ${vox}mm ${sig.orientation} ${sig.datatype}`;
      }),
      String(s.issues.length),
      // One cell, because a variable number of columns makes the file
      // unopenable; the separator is chosen not to collide with prose.
      s.issues.map((i) => `[${i.severity}] ${i.message}`).join(" | "),
      s.dir,
    ].map(csvCell);
  });

  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n") + "\n";
}

/** `sub-cohort-review_2026-09-20.csv` */
export function csvFileName(scan: CohortScan): string {
  const folder = scan.root.split(/[\\/]/).filter(Boolean).pop() ?? "dataset";
  const day = (scan.scannedIso.split("T")[0] ?? "").replace(/-/g, "");
  return `${folder}_review_${day || "scan"}.csv`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(0)} MB`;
  return `${(bytes / 1e3).toFixed(0)} kB`;
}
